import type { Language } from '@packages/config';
import config from '@packages/config';
import { useCustomer } from '@packages/utilities';
import { usePathname } from 'next/navigation';
import type { KimIFrameQueryType, KimPageTypeProps, RatingValue } from './KimRating.types';
import { useI18n } from '../../hooks';

/**
 * Represents the template data for the useIFrameUrl function.
 */
const templateData: {
  [key in KimPageTypeProps]: {
    id: KimIFrameQueryType['x03'];
    title: KimIFrameQueryType['x05'];
    source: KimIFrameQueryType['x07'];
  };
} = {
  storefront: {
    id: '22',
    title: 'BBSTART',
    source: '55',
  },
  detailpage: {
    id: '23',
    title: 'BBDETAIL',
    source: '56',
  },
  serp: {
    id: '24',
    title: 'BBPL',
    source: '57',
  },
};

/**
 * Formats the current date to YYYYMMDDHHMM.
 */
const formattedDate = () => {
  const date = new Date();
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'), // Months are 0-indexed in JavaScript
    String(date.getDate()).padStart(2, '0'),
    String(date.getHours()).padStart(2, '0'),
    String(date.getMinutes()).padStart(2, '0'),
  ].join('');
};

const countryId = () => {
  const {
    i18n: { defaultLocale },
  } = config;
  switch (defaultLocale.substring(3)) {
    case 'AT':
      return '10';
    case 'CH':
      return '20';
    default:
      return '30';
  }
};

/**
 * Custom hook for building the search query for the KIM-iFrame call.
 */
const useBuildSearchQuery = (ratingValue: RatingValue, pageType: KimPageTypeProps) => {
  const { customer } = useCustomer();
  const pathname = usePathname();
  const { language, defaultLanguage } = useI18n();
  const currentLanguage = (language || defaultLanguage) as Language;

  const kid = `${customer?.extra?.kid ? parseInt(customer?.extra?.kid, 10) : 0}`;

  const searchParams: KimIFrameQueryType = {
    i_survey: config.kim.surveyId,
    x02: `${ratingValue}`,
    x03: templateData[pageType].id,
    x04: formattedDate(),
    x05: templateData[pageType].title,
    x06: kid,
    x07: templateData[pageType].source,
    x08: countryId(),
    x09: currentLanguage === 'fr' ? '222' : '111',
    x10: config.kim.tenantId,
    x11: 'x',
    x12: 'x',
    x13: customer?.loginStatus === 'LoggedIn' ? '1' : '0',
    x14: '',
    x15: 'x',
    X16: 'x',
    X17: `${config.headerInformation[currentLanguage]?.canonical || ''}${pathname}`,
    X18: 'x',
    X19: 'x',
    X20: 'x',
    X21: 'x',
    QSL: currentLanguage,
    callback: '',
  };

  // build checksum - sum of selected arguments plus fixed salt 442698
  searchParams.x14 = [
    searchParams.x02,
    searchParams.x03,
    searchParams.x06,
    searchParams.x07,
    searchParams.x08,
    searchParams.x09,
    searchParams.x10,
    searchParams.x13,
  ]
    .reduce((accumulator, currentValue) => accumulator + parseInt(currentValue, 10), 442698)
    .toString();
  return searchParams;
};

/**
 * Custom hook to generate the URL for an iframe.
 * @param options - The options for generating the URL.
 * @returns The generated iframe URL.
 */
export const useIFrameUrl = ({
  ratingValue,
  pageType,
}: {
  ratingValue: RatingValue;
  pageType: KimPageTypeProps;
}) => {
  const searchParams = useBuildSearchQuery(ratingValue, pageType);

  const iFrameUrl = new URL(`https://d438.keyingress.de/`);
  iFrameUrl.search = decodeURIComponent(new URLSearchParams(searchParams).toString());
  return iFrameUrl.href;
};
